import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Home,
    Ticket,
    Users,
    Settings,
    LogOut,
    Shield,
    Code,
    UserCheck,
    Crown,
    Headphones,
    Plus,
    Megaphone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import NotificationBell from '../Dashboard/NotificationBell';
import { getStaffDisplayName } from '../../utils/staffDisplay';
import { useUnreadAnnouncements } from '../../hooks/useUnreadAnnouncements';
import { theme } from '../../lib/theme';
import { useDynamicGradient } from '../../hooks/useDynamicGradient';
import gtaCharacter from '../../assets/gta_character.png';
import gtaCar from '../../assets/gta_car.png';
import gtaGun from '../../assets/gta_gun.png';
import gtaHouse from '../../assets/gta_house.png';
import logoCapital from '../../assets/logo-capital.png';

const UserSidebar = ({ onClose }) => {
    const { user, logout } = useAuth();
    const { unreadCount, hasUnread } = useUnreadAnnouncements();
    const navigate = useNavigate();
    const location = useLocation();
    const [logoIndex, setLogoIndex] = useState(0);
    const logos = [gtaCharacter, gtaCar, gtaGun, gtaHouse, logoCapital];
    const dynamicGradient = useDynamicGradient();

    // Extrai a cor inicial do gradiente para usar nos ícones (ex: from-indigo-400 -> text-indigo-400)
    const getIconColorClass = () => {
        if (!dynamicGradient) return 'text-gray-400';
        const fromClass = dynamicGradient.split(' ').find(c => c.startsWith('from-'));
        return fromClass ? fromClass.replace('from-', 'text-') : 'text-gray-400';
    };

    const iconColorClass = getIconColorClass();

    useEffect(() => {
        if (logoIndex < logos.length - 1) {
            const timer = setTimeout(() => {
                setLogoIndex(prev => prev + 1);
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [logoIndex]);

    const handleLogout = () => {
        logout();
        navigate('/');
    };
    const getNavigationItems = () => {
        const baseItems = [];

        baseItems.push({
            icon: Home,
            label: 'Página Inicial',
            path: '/dashboard/user',
            color: theme.colors.accent.blue
        });

        baseItems.push({
            icon: Users,
            label: 'Personagens',
            path: '/characters',
            color: theme.colors.accent.green
        });

        baseItems.push({
            icon: Plus,
            label: 'Criar Ticket',
            path: '/tickets/create',
            color: theme.colors.accent.green
        });

        baseItems.push({
            icon: Ticket,
            label: 'Meus Tickets',
            path: '/tickets',
            color: theme.colors.accent.blue
        });

        baseItems.push({
            icon: Settings,
            label: 'Configurações',
            path: '/settings',
            color: theme.colors.text.muted
        });

        // Adiciona painel admin/staff unificado para todos os níveis de staff, abaixo de Configurações
        const staffLevels = [
            'admin', 'direcao', 'departamento_juridico',
            'head_staff', 'head_moderacao', 'head_suporte',
            'analista_qualidade', 'programador', 'super_moderador',
            'moderador', 'suporte', 'suporte_compras', 'suporte_descontos',
            'criador_de_conteudo'
        ];

        if (staffLevels.includes(user?.accessLevel)) {
            // Se for admin, mostra ambos
            if (user?.accessLevel === 'admin') {
                baseItems.push({
                    icon: Shield,
                    label: 'Painel Admin',
                    path: '/admin',
                    color: theme.colors.accent.red
                });
                baseItems.push({
                    icon: Shield,
                    label: 'Painel Staff',
                    path: '/staff',
                    color: theme.colors.accent.blue
                });
            } else {
                // Se for outro staff, mostra apenas Painel Staff
                baseItems.push({
                    icon: Shield,
                    label: 'Painel Staff',
                    path: '/staff',
                    color: theme.colors.accent.blue
                });
            }
        }

        return baseItems;
    };

    const navigationItems = getNavigationItems();

    return (
        <div
            className="flex flex-col h-full bg-black/90 backdrop-blur-xl border-r border-white/10 relative transition-all duration-300"
            style={{
                top: 'var(--banner-height, 0px)',
                height: 'calc(100vh - var(--banner-height, 0px))'
            }}
        >
            {/* Header da Sidebar */}
            <div className="p-6 border-b border-white/10 flex flex-col items-center">
                {/* Animated Logo Container */}
                <div className="relative w-32 h-32 mb-4">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={logoIndex}
                            initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
                            animate={{ opacity: 1, scale: 1, rotate: 0 }}
                            exit={{ opacity: 0, scale: 1.1, rotate: 10 }}
                            transition={{ duration: 0.5 }}
                            className="absolute inset-0 rounded-full overflow-hidden border-4 border-white/10 shadow-[0_0_20px_rgba(59,130,246,0.3)] bg-gradient-to-br from-blue-500/20 to-purple-500/20"
                        >
                            <img
                                src={logos[logoIndex]}
                                alt={`Logo sequence ${logoIndex}`}
                                className="w-full h-full object-cover"
                            />
                        </motion.div>
                    </AnimatePresence>
                    {/* Glare effect */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-white/10 via-transparent to-transparent pointer-events-none" />
                </div>

                <div className="text-center">
                    <h2 className="text-xl font-bold text-white mb-1">
                        Grupo Capital
                    </h2>
                    <p className="text-sm text-gray-400">Central de Atendimento</p>
                </div>

                {/* Informações do usuário */}
                <div className="mt-4 p-3 bg-white/5 rounded-lg w-full text-center">
                    <div className="text-sm text-white font-medium truncate">{getStaffDisplayName(user)}</div>
                </div>

                {/* Comunicados - Posição Centralizada Abaixo do Nome */}
                <button
                    onClick={() => {
                        navigate('/announcements');
                        if (onClose) onClose();
                    }}
                    className={`mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl transition-all duration-300 relative group overflow-hidden ${location.pathname === '/announcements'
                        ? 'bg-gradient-to-r from-purple-600/30 to-pink-600/30 text-white border border-purple-500/40 shadow-[0_0_15px_rgba(168,85,247,0.2)]'
                        : 'bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white border border-white/5'
                        }`}
                >
                    <div className="relative flex items-center gap-2 z-10">
                        <Megaphone
                            className={`h-4 w-4 transition-colors ${hasUnread ? 'animate-bounce text-purple-400' : 'text-gray-400 group-hover:text-purple-300'}`}
                        />
                        <span className="text-sm font-bold tracking-wide">COMUNICADOS</span>
                        {hasUnread && (
                            <Badge className="ml-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[10px] h-4 min-w-4 px-1 flex items-center justify-center border-0 shadow-lg shadow-purple-500/20">
                                {unreadCount}
                            </Badge>
                        )}
                    </div>
                    {/* Background Glow */}
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-600/0 via-purple-600/5 to-purple-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </button>
            </div>

            {/* Notification Bell - Absolute Top Right */}
            <div className="absolute top-4 right-4 z-50">
                <NotificationBell />
            </div>

            {/* Navegação */}
            <nav className="flex-1 p-4 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
                <ul className="space-y-2">
                    {navigationItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path;

                        return (
                            <li key={item.path}>
                                <button
                                    onClick={() => {
                                        navigate(item.path);
                                        if (onClose) onClose();
                                    }}
                                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-all duration-200 group ${isActive
                                        ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-white border border-blue-500/30'
                                        : 'text-gray-300 hover:bg-gradient-to-r hover:from-white/5 hover:to-white/10 hover:text-white'
                                        }`}
                                >
                                    <div className="flex items-center space-x-3">
                                        <Icon
                                            className={`h-5 w-5 transition-colors ${item.path === '/announcements' && hasUnread ? 'animate-pulse' : ''} ${isActive ? '' : `${iconColorClass} group-hover:text-white`}`}
                                            style={{ color: isActive ? item.color : undefined }}
                                        />
                                        <span className="font-medium">{item.label}</span>
                                    </div>
                                    {item.badge && (
                                        <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs border-0 animate-pulse">
                                            {item.badge}
                                        </Badge>
                                    )}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </nav>

            {/* Footer da Sidebar */}
            <div className="p-4 border-t border-white/10 space-y-2">
                <Button
                    variant="ghost"
                    onClick={handleLogout}
                    className="w-full justify-start text-gray-300 hover:text-white hover:bg-red-500/10"
                >
                    <LogOut className="h-4 w-4 mr-3" />
                    Sair
                </Button>
            </div>
        </div>
    );
};

export default UserSidebar;
